import React, { useState, useEffect, useCallback } from "react";
import { useUser, useAuth, UserButton } from "@clerk/clerk-react";
import "./AdminPage.css";

const MAX_IMAGE_BYTES = 3 * 1024 * 1024;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const EMPTY_WINE = {
  code: "",
  winename: "",
  photo: "",
  photoUrl: "",
  cepages: "",
  volume: "",
  alcohol: "",
  servingTemp: "",
  pairing: "",
  tastingNote: "",
};

const WINE_FIELDS = [
  { key: "winename", label: "Nom du vin", placeholder: "AOP Gigondas — Cuvée XYZ", flex: 3 },
  { key: "cepages", label: "Cépages", placeholder: "80% Grenache noir, 20% Syrah" },
  { key: "volume", label: "Volume de production", placeholder: "25 000 bouteilles" },
  { key: "alcohol", label: "Teneur en alcool", placeholder: "14.5% vol." },
  { key: "servingTemp", label: "Température de service", placeholder: "16°C" },
  { key: "pairing", label: "Accord mets-vin", placeholder: "Côte de bœuf, mousse au chocolat", textarea: true },
  { key: "tastingNote", label: "Note de dégustation", placeholder: "Arômes, structure, finale...", textarea: true },
];

function WineCard({ wine, workshopCode, authedFetch, onSaved, onDetach }) {
  const isNew = !wine.code;
  const [form, setForm] = useState({ ...EMPTY_WINE, ...wine });
  const [open, setOpen] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);

  const setField = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const previewSrc = form.photoUrl
    ? form.photoUrl
    : form.photo
    ? `/photos/${encodeURIComponent(form.photo)}`
    : null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setStatus({ type: "error", msg: "Veuillez choisir un fichier image." });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setStatus({ type: "error", msg: "Image trop lourde (max 3 Mo)." });
      return;
    }
    setUploading(true);
    setStatus(null);
    try {
      const dataBase64 = await fileToBase64(file);
      const res = await authedFetch("/api/upload-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type,
          dataBase64,
          workshopCode,
          wineCode: form.code,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Échec du téléchargement");
      const { url } = await res.json();
      setForm((f) => ({ ...f, photoUrl: url }));
      setStatus({ type: "success", msg: "Image téléchargée." });
    } catch (err) {
      setStatus({ type: "error", msg: err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.code || !form.winename) {
      setStatus({ type: "error", msg: "Code et nom du vin obligatoires." });
      return;
    }
    setSaving(true);
    setStatus(null);
    try {
      const res = await authedFetch("/api/admin-upsert", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "wine", data: { ...form, code: form.code.toUpperCase() } }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Erreur serveur");
      await onSaved(form.code.toUpperCase());
      setStatus({ type: "success", msg: "Vin enregistré." });
    } catch (e) {
      setStatus({ type: "error", msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wine-card">
      <div className="wine-card-head" onClick={() => setOpen(!open)}>
        <span className="wine-card-code">{form.code || "Nouveau vin"}</span>
        <span className="wine-card-name">{form.winename}</span>
        <span className="wine-card-toggle">{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div className="wine-card-body">
          <div className="form-field" style={{ maxWidth: 160 }}>
            <label>Code du vin</label>
            <input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="RG01"
              disabled={!isNew}
            />
          </div>

          <div className="form-field">
            <label>Photo de la bouteille</label>
            <div className="photo-upload">
              {previewSrc ? (
                <img src={previewSrc} alt="aperçu" className="photo-preview" />
              ) : (
                <div className="photo-preview photo-preview--empty">Aucune image</div>
              )}
              <div className="photo-upload-actions">
                <label className="btn-upload">
                  {uploading ? "Téléchargement…" : previewSrc ? "Remplacer l'image" : "Choisir une image"}
                  <input type="file" accept="image/*" onChange={handleFile} disabled={uploading} hidden />
                </label>
                {form.photoUrl && (
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setForm((f) => ({ ...f, photoUrl: "" }))}
                  >
                    Retirer
                  </button>
                )}
              </div>
            </div>
          </div>

          {WINE_FIELDS.map((f) => (
            <div className="form-field" key={f.key} style={f.flex ? { flex: f.flex } : undefined}>
              <label>{f.label}</label>
              {f.textarea ? (
                <textarea rows={3} value={form[f.key]} onChange={setField(f.key)} placeholder={f.placeholder} />
              ) : (
                <input value={form[f.key]} onChange={setField(f.key)} placeholder={f.placeholder} />
              )}
            </div>
          ))}
          {status && <div className={`admin-feedback ${status.type}`}>{status.msg}</div>}
          <div className="form-actions">
            <button className="btn-save" onClick={handleSave} disabled={saving}>
              {saving ? "Enregistrement…" : "Enregistrer le vin"}
            </button>
            <button className="btn-cancel" onClick={() => onDetach(form.code)}>
              {isNew ? "Annuler" : "Retirer de l'atelier"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function WorkshopEditor({ workshop, allWines, authedFetch, refresh }) {
  const isNew = !workshop;
  const [form, setForm] = useState({ code: "", name: "", date: "" });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [addingWine, setAddingWine] = useState(false);

  useEffect(() => {
    setForm({
      code: workshop?.code || "",
      name: workshop?.name || "",
      date: workshop?.date ? workshop.date.slice(0, 10) : "",
    });
    setAddingWine(false);
    setStatus(null);
  }, [workshop]);

  const wineCodes = workshop?.wineCodes || [];
  const workshopWines = wineCodes.map(
    (code) => allWines.find((w) => w.code === code) || { code }
  );

  const upsertWorkshop = async (data) => {
    const res = await authedFetch("/api/admin-upsert", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "workshop", data }),
    });
    if (!res.ok) throw new Error((await res.json()).error || "Erreur serveur");
  };

  const handleSaveWorkshop = async () => {
    if (!form.code || !form.name) {
      setStatus({ type: "error", msg: "Code et nom de l'atelier obligatoires." });
      return;
    }
    setSaving(true);
    setStatus(null);
    try {
      await upsertWorkshop({
        code: form.code.toUpperCase(),
        name: form.name,
        date: form.date || null,
        wineCodes: workshop?.wineCodes || [],
      });
      setStatus({ type: "success", msg: "Atelier enregistré." });
      await refresh(form.code.toUpperCase());
    } catch (e) {
      setStatus({ type: "error", msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  // After a wine is saved, ensure its code is attached to this workshop.
  const handleWineSaved = async (wineCode) => {
    if (!wineCodes.includes(wineCode)) {
      await upsertWorkshop({ code: workshop.code, wineCodes: [...wineCodes, wineCode] });
    }
    setAddingWine(false);
    await refresh(workshop.code);
  };

  const handleDetachWine = async (wineCode) => {
    await upsertWorkshop({ code: workshop.code, wineCodes: wineCodes.filter((c) => c !== wineCode) });
    await refresh(workshop.code);
  };

  return (
    <div className="workshop-editor">
      <div className="admin-form-card">
        <h3>{isNew ? "Nouvel atelier" : `Atelier ${form.code}`}</h3>
        <div className="admin-form">
          <div className="form-row">
            <div className="form-field" style={{ maxWidth: 160 }}>
              <label>Code d'accès</label>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="RG1"
                disabled={!isNew}
              />
            </div>
            <div className="form-field" style={{ flex: 2 }}>
              <label>Nom de l'atelier</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Atelier Juin 2026" />
            </div>
            <div className="form-field">
              <label>Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>
          {status && <div className={`admin-feedback ${status.type}`}>{status.msg}</div>}
          <div className="form-actions">
            <button className="btn-save" onClick={handleSaveWorkshop} disabled={saving}>
              {saving ? "Enregistrement…" : "Enregistrer l'atelier"}
            </button>
          </div>
        </div>
      </div>

      {!isNew && (
        <div className="admin-form-card">
          <h3>Vins de l'atelier ({workshopWines.length})</h3>
          {workshopWines.length === 0 && !addingWine && (
            <p style={{ color: "#aaa", fontSize: 14 }}>Aucun vin. Ajoutez-en un ci-dessous.</p>
          )}
          {workshopWines.map((w) => (
            <WineCard
              key={w.code}
              wine={w}
              workshopCode={form.code}
              authedFetch={authedFetch}
              onSaved={handleWineSaved}
              onDetach={handleDetachWine}
            />
          ))}

          {addingWine ? (
            <WineCard
              wine={EMPTY_WINE}
              workshopCode={form.code}
              authedFetch={authedFetch}
              onSaved={handleWineSaved}
              onDetach={() => setAddingWine(false)}
            />
          ) : (
            <button className="btn-add-wine" onClick={() => setAddingWine(true)}>+ Ajouter un vin</button>
          )}
        </div>
      )}
    </div>
  );
}

const AdminPage = () => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [workshops, setWorkshops] = useState([]);
  const [wines, setWines] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const adminEmails = (process.env.REACT_APP_ADMIN_EMAIL || "")
    .split(",")
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
  const isAdmin =
    adminEmails.length === 0 ||
    adminEmails.includes(user?.primaryEmailAddress?.emailAddress?.toLowerCase());

  const authedFetch = useCallback(
    async (url, options = {}) => {
      const token = await getToken();
      return fetch(url, {
        ...options,
        headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
      });
    },
    [getToken]
  );

  const fetchData = useCallback(
    async (selectAfter) => {
      setLoading(true);
      setError(null);
      try {
        const res = await authedFetch("/api/admin-list");
        if (!res.ok) throw new Error((await res.json()).error || `Erreur ${res.status}`);
        const data = await res.json();
        setWorkshops(data.workshops || []);
        setWines(data.wines || []);
        if (selectAfter) {
          setSelectedCode(selectAfter);
          setCreating(false);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [authedFetch]
  );

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [isAdmin, fetchData]);

  if (!isAdmin) {
    return (
      <div className="admin-denied">
        <p>Accès non autorisé.</p>
      </div>
    );
  }

  const selectedWorkshop = creating ? null : workshops.find((w) => w.code === selectedCode) || null;

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Administration — Petits Secrets de Vins</h1>
        <UserButton afterSignOutUrl="/sign-in" />
      </div>

      {error && <div className="admin-feedback error" style={{ margin: "0 28px 16px" }}>{error}</div>}

      <div className="admin-layout">
        <aside className="admin-sidebar">
          <button className="btn-new-workshop" onClick={() => { setCreating(true); setSelectedCode(null); }}>
            + Nouvel atelier
          </button>
          {loading && <p style={{ color: "#888", fontSize: 14 }}>Chargement…</p>}
          {workshops.map((w) => (
            <button
              key={w.code}
              className={`workshop-item ${!creating && selectedCode === w.code ? "active" : ""}`}
              onClick={() => { setSelectedCode(w.code); setCreating(false); }}
            >
              <span className="workshop-item-code">{w.code}</span>
              <span className="workshop-item-name">{w.name}</span>
              <span className="workshop-item-count">{(w.wineCodes || []).length} vin(s)</span>
            </button>
          ))}
        </aside>

        <main className="admin-main">
          {creating || selectedWorkshop ? (
            <WorkshopEditor
              key={selectedWorkshop?.code || "new"}
              workshop={selectedWorkshop}
              allWines={wines}
              authedFetch={authedFetch}
              refresh={fetchData}
            />
          ) : (
            <div className="admin-empty">
              <p>Sélectionnez un atelier ou créez-en un nouveau.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminPage;
